import type { StorageGroupMode } from './StorageHierarchy';

const STORAGE_HIERARCHY_KEY_PREFIX = 'tcvs:storage-hierarchy-expanded';
const STORAGE_GROUP_MODE_KEY_PREFIX = 'tcvs:storage-group-mode';
const MAX_SAVED_KEYS = 250;

export interface StorageHierarchyStateStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function storageHierarchyStateKey(surface: string, reviewerId?: string): string {
  return `${STORAGE_HIERARCHY_KEY_PREFIX}:${encodeURIComponent(reviewerId ?? 'anonymous')}:${encodeURIComponent(surface)}`;
}

export function readStorageHierarchyExpansion(
  store: StorageHierarchyStateStore | undefined,
  surface: string,
  reviewerId?: string,
): Set<string> {
  if (store === undefined) return new Set();
  try {
    const parsed = JSON.parse(store.getItem(storageHierarchyStateKey(surface, reviewerId)) ?? '[]');
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .slice(0, MAX_SAVED_KEYS),
    );
  } catch {
    return new Set();
  }
}

export function writeStorageHierarchyExpansion(
  store: StorageHierarchyStateStore | undefined,
  surface: string,
  reviewerId: string | undefined,
  expanded: ReadonlySet<string>,
): void {
  if (store === undefined) return;
  try {
    store.setItem(
      storageHierarchyStateKey(surface, reviewerId),
      JSON.stringify([...expanded].slice(0, MAX_SAVED_KEYS)),
    );
  } catch {
    // Storage can be unavailable in privacy-restricted browser sessions.
  }
}

export function browserStorageHierarchyStateStore(): StorageHierarchyStateStore | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function storageGroupModeStateKey(surface: string, reviewerId?: string): string {
  return `${STORAGE_GROUP_MODE_KEY_PREFIX}:${encodeURIComponent(reviewerId ?? 'anonymous')}:${encodeURIComponent(surface)}`;
}

export function readStorageGroupMode(
  store: StorageHierarchyStateStore | undefined,
  surface: string,
  reviewerId: string | undefined,
  defaultMode: StorageGroupMode,
): StorageGroupMode {
  if (store === undefined) return defaultMode;
  try {
    const value = store.getItem(storageGroupModeStateKey(surface, reviewerId));
    return value === 'shelf' ||
      value === 'box' ||
      value === 'row' ||
      value === 'batch' ||
      value === 'none'
      ? value
      : defaultMode;
  } catch {
    return defaultMode;
  }
}

export function writeStorageGroupMode(
  store: StorageHierarchyStateStore | undefined,
  surface: string,
  reviewerId: string | undefined,
  mode: StorageGroupMode,
): void {
  if (store === undefined) return;
  try {
    store.setItem(storageGroupModeStateKey(surface, reviewerId), mode);
  } catch {
    // Storage can be unavailable in privacy-restricted browser sessions.
  }
}
